import { Money } from '@arabat/money';
import { receiptHtmlParts as h } from './receipt-html.js';

/**
 * An A4 invoice for an online order (owner, 2026-10-25): printed from the online console and
 * saved as PDF. Labels are Arabic with English underneath; dates arrive already formatted in
 * the merchant's zone so the template stays pure. Empty fields are left out.
 */
export type InvoiceData = {
  storeName: string;
  logoUrl: string | null;
  /** The store's brand colour (hex); anything else falls back to the default ink. */
  accent: string | null;
  storePhone: string | null;
  storeEmail: string | null;
  branchName: string | null;
  branchAddress: string | null;
  branchPhone: string | null;
  number: string;
  receiptNumber: string | null;
  issuedAt: string;
  state: 'paid' | 'unpaid' | 'cancelled';
  paidAt: string | null;
  paymentMethod: string | null;
  paymentReference: string | null;
  fulfillment: 'pickup' | 'delivery';
  wanted: string | null;
  customerName: string;
  customerPhone: string | null;
  customerEmail: string | null;
  deliveryArea: string | null;
  deliveryAddress: string | null;
  note: string | null;
  lines: {
    name: string;
    detail: string | null;
    /** The shopper's choices as "Group: choice" (ADR-0160); their prices are in the unit price. */
    options?: string[];
    /** The shopper's note for this item (ADR-0160). */
    note?: string | null;
    quantity: string;
    unitPriceMinor: bigint;
    totalMinor: bigint;
  }[];
  subtotalMinor: bigint;
  discountMinor: bigint;
  promotion: string | null;
  deliveryFeeMinor: bigint;
  totalMinor: bigint;
};

const { escape } = h;
const DEFAULT_ACCENT = '#1f2937';

const amount = (minor: bigint) => Money.ofMinor(minor, 'KWD').toDecimalString();
const kwd = (minor: bigint) => `<bdi dir="ltr">${amount(minor)}</bdi> <span class="cur">د.ك</span>`;

/** Black or white text, whichever reads on the brand colour. */
function inkOn(hex: string): string {
  const full =
    hex.length === 4
      ? hex
          .slice(1)
          .split('')
          .map((c) => c + c)
          .join('')
      : hex.slice(1, 7);
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16) / 255) as [
    number,
    number,
    number,
  ];
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const luminance = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return luminance > 0.45 ? '#111827' : '#ffffff';
}

/** A bilingual label: Arabic, then the English word in small grey type. */
const label = (ar: string, en: string) =>
  `<span class="ar">${escape(ar)}</span><span class="en" dir="ltr">${escape(en)}</span>`;

/** A label / value row; null values are skipped. */
function field(ar: string, en: string, value: string | null, ltr = false) {
  if (!value) return '';
  return `<div class="field"><div class="k">${label(ar, en)}</div><div class="v" dir="${ltr ? 'ltr' : 'auto'}">${escape(value)}</div></div>`;
}

const STATE: Record<InvoiceData['state'], [string, string]> = {
  paid: ['مدفوعة', 'Paid'],
  unpaid: ['غير مدفوعة', 'Unpaid'],
  cancelled: ['ملغاة', 'Cancelled'],
};

const FULFILLMENT: Record<InvoiceData['fulfillment'], string> = {
  pickup: 'استلام من الفرع / Pickup',
  delivery: 'توصيل / Delivery',
};

function style(accent: string) {
  const ink = inkOn(accent);
  return `
@page { size: A4; margin: 0; }
* { box-sizing: border-box; }
html, body { margin: 0; background: #fff; }
body { font-family: 'IBM Plex Sans Arabic', 'Segoe UI', Tahoma, Arial, sans-serif; color: #111827;
  font-size: 12.5px; line-height: 1.45; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.page { width: 210mm; min-height: 297mm; padding: 14mm 14mm 12mm; display: flex; flex-direction: column; gap: 7mm; }
p { margin: 0; }
.en { display: block; font-size: 9.5px; color: #6b7280; letter-spacing: 0; }
.head { display: flex; justify-content: space-between; align-items: flex-start; gap: 8mm;
  padding-bottom: 5mm; border-bottom: 2px solid ${accent}; }
.brand { display: flex; align-items: center; gap: 4mm; min-width: 0; }
.brand img { max-height: 22mm; max-width: 40mm; object-fit: contain; }
.brand .name { font-size: 20px; font-weight: 700; }
.brand .contact { color: #4b5563; font-size: 11.5px; }
.title { text-align: left; }
.title h1 { margin: 0; font-size: 26px; font-weight: 700; color: ${accent}; line-height: 1.1; }
.title .sub { font-size: 12px; color: #6b7280; letter-spacing: 2px; }
.title .meta { margin-top: 2mm; font-size: 12px; }
.title .number { font-size: 15px; font-weight: 700; }
.state { display: inline-block; margin-top: 2mm; padding: 1mm 3mm; border-radius: 99px; font-weight: 700; font-size: 11.5px; }
.state.paid { background: #dcfce7; color: #166534; }
.state.unpaid { background: #fef3c7; color: #92400e; }
.state.cancelled { background: #fee2e2; color: #991b1b; }
.boxes { display: grid; grid-template-columns: 1fr 1fr; gap: 5mm; }
.box { border: 1px solid #e5e7eb; border-radius: 3mm; padding: 4mm; }
.box h2 { margin: 0 0 2mm; font-size: 13px; color: ${accent}; }
.box h2 .en { display: inline; margin-right: 2mm; }
.field { display: flex; gap: 3mm; padding: 0.8mm 0; }
.field .k { flex: 0 0 28mm; color: #4b5563; }
.field .k .en { display: none; }
.field .v { flex: 1; min-width: 0; overflow-wrap: anywhere; white-space: pre-line; text-align: right; }
table { width: 100%; border-collapse: collapse; }
thead tr { background: ${accent}; }
thead th { color: ${ink}; font-weight: 600; padding: 2.2mm 3mm; text-align: right; }
thead th .en { color: ${ink}; opacity: 0.8; }
tbody td p { text-align: right; }
tbody td { padding: 2.4mm 3mm; border-bottom: 1px solid #e5e7eb; vertical-align: top; }
tbody tr { break-inside: avoid; }
td.n, th.n { text-align: left; white-space: nowrap; }
td.i { color: #6b7280; width: 8mm; }
.detail { color: #6b7280; font-size: 11px; }
.bottom { display: flex; justify-content: space-between; align-items: flex-start; gap: 8mm; }
.note { flex: 1; color: #374151; }
.note .box { white-space: pre-line; }
.totals { flex: 0 0 78mm; margin-inline-start: auto; }
.totals .row { display: flex; justify-content: space-between; gap: 4mm; padding: 1.6mm 3mm; }
.totals .row .en { display: inline; margin-right: 1.5mm; }
.totals .grand { background: ${accent}; color: ${ink}; border-radius: 2mm; font-size: 15px; font-weight: 700; margin-top: 1mm; }
.totals .grand .en { color: ${ink}; opacity: 0.8; }
.totals .due { font-weight: 700; }
.cur { font-size: 0.85em; }
.foot { margin-top: auto; padding-top: 4mm; border-top: 1px solid #e5e7eb; text-align: center; color: #6b7280; font-size: 11px; }
.foot .thanks { color: #111827; font-size: 13px; font-weight: 600; }
`;
}

/** The invoice as one self-contained A4 HTML document (inline style; `extraCss` adds fonts). */
export function renderInvoiceHtml(data: InvoiceData, extraCss = ''): string {
  const accent =
    data.accent && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(data.accent) ? data.accent : DEFAULT_ACCENT;
  const [stateAr, stateEn] = STATE[data.state];
  const contact = [data.storePhone, data.storeEmail].filter(Boolean) as string[];
  const head = `<header class="head">
<div class="brand">${data.logoUrl ? `<img src="${escape(data.logoUrl)}" alt="">` : ''}<div>
<p class="name" dir="auto">${escape(data.storeName)}</p>
${contact.length ? `<p class="contact" dir="ltr">${contact.map(escape).join(' · ')}</p>` : ''}
${data.branchAddress ? `<p class="contact" dir="auto">${escape(data.branchAddress)}</p>` : ''}
</div></div>
<div class="title"><h1>فاتورة</h1><p class="sub">INVOICE</p>
<p class="meta number" dir="ltr">${escape(data.number)}</p>
<p class="meta" dir="auto">${escape(data.issuedAt)}</p>
<span class="state ${data.state}">${stateAr} · ${stateEn}</span></div>
</header>`;
  const customer = [
    field('الاسم', 'Name', data.customerName),
    field('الهاتف', 'Phone', data.customerPhone, true),
    field('البريد', 'E-mail', data.customerEmail, true),
    field('المنطقة', 'Area', data.deliveryArea),
    field('العنوان', 'Address', data.deliveryAddress),
  ].join('');
  const order = [
    field('رقم الفاتورة', 'Invoice no.', data.number, true),
    field('رقم الإيصال', 'Receipt no.', data.receiptNumber, true),
    field('نوع الطلب', 'Fulfilment', FULFILLMENT[data.fulfillment]),
    field('الفرع', 'Branch', data.branchName),
    field('هاتف الفرع', 'Branch phone', data.branchPhone, true),
    field('الموعد', 'Requested', data.wanted),
    field('طريقة الدفع', 'Payment', data.paymentMethod),
    field('مرجع الدفع', 'Reference', data.paymentReference, true),
    field('تاريخ الدفع', 'Paid at', data.paidAt),
  ].join('');
  const rows = data.lines
    .map(
      (l, i) => `<tr>
<td class="i">${i + 1}</td>
<td><p dir="auto">${escape(l.name)}</p>${l.detail ? `<p class="detail" dir="auto">${escape(l.detail)}</p>` : ''}${l.options?.length ? `<p class="detail" dir="auto">${escape(l.options.join('، '))}</p>` : ''}${l.note ? `<p class="detail" dir="auto">«${escape(l.note)}»</p>` : ''}</td>
<td class="n" dir="ltr">${escape(l.quantity)}</td>
<td class="n">${kwd(l.unitPriceMinor)}</td>
<td class="n">${kwd(l.totalMinor)}</td>
</tr>`,
    )
    .join('');
  const due = data.state === 'unpaid' ? data.totalMinor : 0n;
  const totals = [
    `<div class="row"><span>${label('المجموع', 'Subtotal')}</span><span>${kwd(data.subtotalMinor)}</span></div>`,
    data.discountMinor > 0n
      ? `<div class="row"><span>${label('الخصم', 'Discount')}${data.promotion ? ` <span class="detail" dir="auto">(${escape(data.promotion)})</span>` : ''}</span><span>-${kwd(data.discountMinor)}</span></div>`
      : '',
    data.fulfillment === 'delivery'
      ? `<div class="row"><span>${label('رسوم التوصيل', 'Delivery')}</span><span>${kwd(data.deliveryFeeMinor)}</span></div>`
      : '',
    `<div class="row grand"><span>${label('الإجمالي', 'Total')}</span><span>${kwd(data.totalMinor)}</span></div>`,
    data.state === 'cancelled'
      ? ''
      : `<div class="row due"><span>${label('المستحق', 'Amount due')}</span><span>${kwd(due)}</span></div>`,
  ].join('');
  return [
    '<!doctype html>',
    '<html lang="ar" dir="rtl"><head><meta charset="utf-8">',
    `<title>${escape(`فاتورة ${data.number}`)}</title>`,
    `<style>${extraCss}${style(accent)}</style></head><body><main class="page">`,
    head,
    `<section class="boxes"><div class="box"><h2>${label('العميل', 'Bill to')}</h2>${customer}</div>`,
    `<div class="box"><h2>${label('الطلب', 'Order')}</h2>${order}</div></section>`,
    `<table><thead><tr><th>#</th><th>${label('الصنف', 'Item')}</th><th class="n">${label('الكمية', 'Qty')}</th><th class="n">${label('سعر الوحدة', 'Unit price')}</th><th class="n">${label('الإجمالي', 'Amount')}</th></tr></thead><tbody>${rows}</tbody></table>`,
    `<section class="bottom"><div class="note">${data.note ? `<div class="box"><h2>${label('ملاحظة', 'Note')}</h2><p dir="auto">${escape(data.note)}</p></div>` : ''}</div>`,
    `<div class="totals">${totals}</div></section>`,
    `<footer class="foot"><p class="thanks">شكراً لطلبكم · Thank you for your order</p>${data.storeName ? `<p dir="auto">${escape(data.storeName)}</p>` : ''}</footer>`,
    '</main></body></html>',
  ].join('');
}
